import React, { useEffect } from "react";
import { typeList } from "@adgm/cross-models";
import {
  getKycInformationNationalityValue,
  formatRecordToTableData,
  formatArrayValue,
  formatListedEntity,
  i18nCrossUtil,
} from "@adgm/libs";
import { KycKeyValueTable } from "@adgm/cross-ui";
import type { TKycInfoV2 } from "@adgm/cross-models";

interface IProps {
  kycInfo: TKycInfoV2;
  getKYCInfo: () => void;
}

// 表格组件 仅用于web端
const KycInformationTable = ({
  columns,
  data,
  crossIntl,
}: {
  columns: { label: string; name: string }[];
  data: Record<string, unknown>[];
  crossIntl: Record<string, string>;
}) => {
  if (!data || data.length === 0) {
    return <div className="text-gray-500">{crossIntl["kyc.v2.display.table.no.data"]}</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr style={{ backgroundColor: "#2F3342" }}>
            {columns.map((column, index) => (
              <th
                key={index}
                className="px-4 py-3 text-left font-medium text-white text-sm"
                style={{ border: "1px solid #42485C" }}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column, colIndex) => (
                <td
                  key={colIndex}
                  className="px-4 py-3 text-white text-sm text-left whitespace-pre-line"
                  style={{ border: "1px solid #42485C" }}>
                  {getKycInformationNationalityValue(row[column.name], column.name) || "-"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const PageKycInformationContentV2: React.FC<IProps> = props => {
  const { kycInfo } = props;
  const crossIntl = i18nCrossUtil.t();

  useEffect(() => {
    props.getKYCInfo();
  }, []);

  // 机构类型渲染
  if (kycInfo.type === typeList.Institutions) {
    const institution = kycInfo.institution;
    const questionnaire = kycInfo.institutionSurveyQuestionnaire;

    return (
      <div className="">
        {/* Onboarding Questionnaire */}
        {questionnaire ? (
          <div className="mb-8">
            <div className="text-[24px] font-bold mb-6">
              {crossIntl["kyc.v2.display.section.onboarding.questionnaire"]}
            </div>
            <div style={{ whiteSpace: "pre-line" }}>
              <KycKeyValueTable
                data={[
                  {
                    label: crossIntl["kyc.v2.display.label.intend.to.deposit"],
                    value: questionnaire.intendedDepositAmount || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.volume.of.trades"],
                    value: questionnaire.expectedTradeVolume || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.expected.account.utilization"],
                    value: formatArrayValue(questionnaire.expectedAccountUtilization),
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.intend.to.fund.account"],
                    value: formatArrayValue(questionnaire.fundingMethods),
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.funding.account.owner.name"],
                    value: questionnaire.fundingAccountOwnerName || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.account.from"],
                    value: getKycInformationNationalityValue(
                      questionnaire.fundingSourceCountry,
                      "fundingSourceCountry",
                    ),
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.bank.virtual"],
                    value: questionnaire.depositingInstitution || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.types.of.product"],
                    value: formatArrayValue(questionnaire.investmentProductTypes),
                  },
                ]}
              />
            </div>
          </div>
        ) : null}

        {/* Registration information of company */}
        {institution?.registration ? (
          <div className="mb-8">
            <div className="text-[24px] font-bold mb-6">
              {crossIntl["kyc.v2.display.section.registration"]}
            </div>
            <div style={{ whiteSpace: "pre-line" }}>
              <KycKeyValueTable
                data={[
                  {
                    label: crossIntl["kyc.v2.display.label.company.name"],
                    value: institution.registration.companyName || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.trading.name"],
                    value: institution.registration.tradingName || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.date.of.incorporation"],
                    value: institution.registration.incorporationDate || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.place.of.incorporation"],
                    value: getKycInformationNationalityValue(
                      institution.registration.incorporationPlace,
                      "incorporationPlace",
                    ),
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.registered.address"],
                    value: institution.registration.registeredAddress || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.main.business.address"],
                    value: institution.registration.mainBusinessAddress || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.legal.form"],
                    value: institution.registration.legalForm || "-",
                  },
                ]}
              />
            </div>
          </div>
        ) : null}

        {/* Operational history of company */}
        {institution?.operations ? (
          <div className="mb-8">
            <div className="text-[24px] font-bold mb-6">
              {crossIntl["kyc.v2.display.section.operations"]}
            </div>
            <div style={{ whiteSpace: "pre-line" }}>
              <KycKeyValueTable
                data={[
                  {
                    label: crossIntl["kyc.v2.display.label.commercial.activity"],
                    value: institution.operations.commercialActivities || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.years.in.operation"],
                    value: institution.operations.yearsInOperation || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.number.of.staff"],
                    value: institution.operations.numberOfStaff || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.operating.countries"],
                    value: getKycInformationNationalityValue(
                      institution.operations.operatingCountries,
                      "operatingCountries",
                    ),
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.most.recent.net.profit"],
                    value: institution.operations.mostRecentNetProfit || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.most.recent.net.asset"],
                    value: institution.operations.mostRecentNetAsset || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.financial.year"],
                    value: institution.operations.financialYearForEstimates || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.listed.entity"],
                    value: institution.operations.listedEntity
                      ? formatListedEntity(institution.operations.listedEntity)
                      : crossIntl["kyc.v2.option.label.no"],
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.uae.wholly.owned"],
                    value: institution.operations.uaeWhollyOwnedHolding || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.politically.exposed.stakeholders"],
                    value: institution.operations.politicallyExposedStakeholders || "-",
                  },
                ]}
              />
            </div>
          </div>
        ) : null}

        {/* Shareholder */}
        {institution?.shareholders && institution.shareholders.length > 0 ? (
          <div className="mb-8">
            <div className="text-[24px] font-bold mb-6">
              {crossIntl["kyc.v2.display.section.shareholder"]}
            </div>
            <KycInformationTable
              columns={[
                { label: crossIntl["kyc.v2.display.label.shareholder.name"], name: "name" },
                {
                  label: crossIntl["kyc.v2.display.label.percentage.of.shares.held"],
                  name: "shareholdingPercentage",
                },
                { label: crossIntl["kyc.v2.display.label.nationality"], name: "nationalities" },
              ]}
              data={institution.shareholders.map(item => ({
                name: item.name || "-",
                shareholdingPercentage: item.shareholdingPercentage || "-",
                nationalities: item.nationalities, // 保留原始值，让表格组件处理
              }))}
              crossIntl={crossIntl}
            />
          </div>
        ) : null}

        {/* Ultimate Beneficiary Owner */}
        {institution?.owner?.beneficiaries && institution.owner.beneficiaries.length > 0 ? (
          <div className="mb-8">
            <div className="text-[24px] font-bold mb-6">
              {crossIntl["kyc.v2.display.section.beneficiary.owner"]}
            </div>
            <KycInformationTable
              columns={[
                { label: crossIntl["kyc.v2.display.label.owner.name"], name: "uboName" },
                {
                  label: crossIntl["kyc.v2.display.label.percentage.of.shares.held"],
                  name: "shareholdingPercentage",
                },
                { label: crossIntl["kyc.v2.display.label.nationality"], name: "nationalities" },
                {
                  label: crossIntl["kyc.v2.display.label.residential.address"],
                  name: "residentialAddress",
                },
              ]}
              data={institution.owner.beneficiaries.map(item => ({
                uboName: item.uboName || "-",
                shareholdingPercentage: item.shareholdingPercentage || "-",
                nationalities: item.nationalities, // 保留原始值，让表格组件处理
                residentialAddress: item.residentialAddress || "-",
              }))}
              crossIntl={crossIntl}
            />
          </div>
        ) : null}

        {/* Source of wealth - 根据不同的财富来源类型展示 */}
        {institution?.owner?.wealthSourceOfOwner ? (
          <div className="mb-8">
            {/* 工资/奖金 */}
            {institution.owner.wealthSourceOfOwner.salary ? (
              <>
                <div className="text-[24px] font-bold mb-6">
                  {crossIntl["kyc.v2.display.section.source.of.wealth.salary"]}
                </div>
                <div style={{ whiteSpace: "pre-line", marginBottom: 32 }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.display.label.employer.name"],
                        value: (institution.owner.wealthSourceOfOwner.salary as any)?.name || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.employer.address"],
                        value:
                          (institution.owner.wealthSourceOfOwner.salary as any)?.address || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.business.sector"],
                        value:
                          (institution.owner.wealthSourceOfOwner.salary as any)?.businessSector ||
                          "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.salary.per.annum"],
                        value:
                          (institution.owner.wealthSourceOfOwner.salary as any)?.salaryPerAnnum ||
                          "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.years.working.at.employer"],
                        value:
                          (institution.owner.wealthSourceOfOwner.salary as any)?.yearsOfService ||
                          "-",
                      },
                    ]}
                  />
                </div>
              </>
            ) : null}

            {/* 自营职业和商业所有权 */}
            {institution.owner.wealthSourceOfOwner.selfEmployment ? (
              <>
                <div className="text-[24px] font-bold mb-6">
                  {crossIntl["kyc.v2.display.section.source.of.wealth.self.employment"]}
                </div>
                <div style={{ whiteSpace: "pre-line", marginBottom: 32 }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.name.and.address.of.business"],
                        value:
                          institution.owner.wealthSourceOfOwner.selfEmployment.nameAndAddress ||
                          "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.annual.profit"],
                        value:
                          institution.owner.wealthSourceOfOwner.selfEmployment.annualProfit || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.nature.of.business"],
                        value:
                          institution.owner.wealthSourceOfOwner.selfEmployment.businessNature ||
                          "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.ownership.percentage.in.business"],
                        value:
                          (institution.owner.wealthSourceOfOwner.selfEmployment as any)
                            ?.percentage || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.business.years.in.operation"],
                        value:
                          institution.owner.wealthSourceOfOwner.selfEmployment
                            .businessYearsInOperation || "-",
                      },
                    ]}
                  />
                </div>
              </>
            ) : null}

            {/* 资产出售 */}
            {institution.owner.wealthSourceOfOwner.assetSale ? (
              <>
                <div className="text-[24px] font-bold mb-6">
                  {crossIntl["kyc.v2.display.section.source.of.wealth.asset.sale"]}
                </div>
                <div style={{ whiteSpace: "pre-line", marginBottom: 32 }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.type.of.asset"],
                        value: institution.owner.wealthSourceOfOwner.assetSale.assetType || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.date.of.sale"],
                        value: institution.owner.wealthSourceOfOwner.assetSale.saleDate || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.total.sale.amount"],
                        value: institution.owner.wealthSourceOfOwner.assetSale.saleAmount || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.how.long.asset.owned"],
                        value: institution.owner.wealthSourceOfOwner.assetSale.holdPeriod || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.originating.source.of.asset"],
                        value:
                          institution.owner.wealthSourceOfOwner.assetSale.originatingSource || "-",
                      },
                    ]}
                  />
                </div>
              </>
            ) : null}

            {/* 继承 */}
            {institution.owner.wealthSourceOfOwner.inheritance ? (
              <>
                <div className="text-[24px] font-bold mb-6">
                  {crossIntl["kyc.v2.display.section.source.of.wealth.inheritance"]}
                </div>
                <div style={{ whiteSpace: "pre-line", marginBottom: 32 }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.deceased.name"],
                        value:
                          institution.owner.wealthSourceOfOwner.inheritance.deceasedName || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.date.of.death"],
                        value: institution.owner.wealthSourceOfOwner.inheritance.dateOfDeath || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.relationship.to.customer"],
                        value:
                          institution.owner.wealthSourceOfOwner.inheritance.relationship || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.inheritance.received"],
                        value:
                          institution.owner.wealthSourceOfOwner.inheritance.receivedInheritance ||
                          "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.date.of.receipt"],
                        value:
                          institution.owner.wealthSourceOfOwner.inheritance.inheritanceDate || "-",
                      },
                    ]}
                  />
                </div>
              </>
            ) : null}

            {/* 投资组合管理（Profit） */}
            {institution.owner.wealthSourceOfOwner.investmentPortfolioManagement ? (
              <>
                <div className="text-[24px] font-bold mb-6">
                  {crossIntl["kyc.v2.display.section.source.of.wealth.profits"]}
                </div>
                <div style={{ whiteSpace: "pre-line", marginBottom: 32 }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.display.label.date.of.receipt.of.dividend"],
                        value:
                          institution.owner.wealthSourceOfOwner.investmentPortfolioManagement
                            .dividendReceiptDate || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.display.label.total.amount.received"],
                        value:
                          institution.owner.wealthSourceOfOwner.investmentPortfolioManagement
                            .amountReceived || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.display.label.company.paying.dividend"],
                        value:
                          institution.owner.wealthSourceOfOwner.investmentPortfolioManagement
                            .payingCompanyName || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.display.label.length.of.time.shares.held"],
                        value:
                          institution.owner.wealthSourceOfOwner.investmentPortfolioManagement
                            .holdPeriod || "-",
                      },
                    ]}
                  />
                </div>
              </>
            ) : null}

            {/* 赠与 */}
            {institution.owner.wealthSourceOfOwner.gift ? (
              <>
                <div className="text-[24px] font-bold mb-6">
                  {crossIntl["kyc.v2.display.section.source.of.wealth.gifts"]}
                </div>
                <div style={{ whiteSpace: "pre-line", marginBottom: 32 }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.date.of.receipt"],
                        value: institution.owner.wealthSourceOfOwner.gift.receiptDate || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.display.label.total.amount.received"],
                        value: institution.owner.wealthSourceOfOwner.gift.amountReceived || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.relationship.to.ubo"],
                        value: institution.owner.wealthSourceOfOwner.gift.relationship || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.reason.for.gift"],
                        value: institution.owner.wealthSourceOfOwner.gift.giftReason || "-",
                      },
                    ]}
                  />
                </div>
              </>
            ) : null}

            {/* 法律和解 */}
            {institution.owner.wealthSourceOfOwner.legalSettlements ? (
              <>
                <div className="text-[24px] font-bold mb-6">
                  {crossIntl["kyc.v2.display.section.source.of.wealth.legal.settlements"]}
                </div>
                <div style={{ whiteSpace: "pre-line", marginBottom: 32 }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.details.of.legal.settlement"],
                        value: institution.owner.wealthSourceOfOwner.legalSettlements.detail || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.payment.issuing.party"],
                        value:
                          institution.owner.wealthSourceOfOwner.legalSettlements
                            .paymentIssuingCountry || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.settlement.amount"],
                        value:
                          institution.owner.wealthSourceOfOwner.legalSettlements.settlementAmount ||
                          "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.settlement.date"],
                        value:
                          institution.owner.wealthSourceOfOwner.legalSettlements.settlementDate ||
                          "-",
                      },
                    ]}
                  />
                </div>
              </>
            ) : null}

            {/* 奖金/意外之财 */}
            {institution.owner.wealthSourceOfOwner.winnings ? (
              <>
                <div className="text-[24px] font-bold mb-6">
                  {crossIntl["kyc.v2.display.section.source.of.wealth.winnings"]}
                </div>
                <div style={{ whiteSpace: "pre-line", marginBottom: 32 }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.date.of.winning.windfall"],
                        value: institution.owner.wealthSourceOfOwner.winnings.date || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.nature.of.windfall"],
                        value: institution.owner.wealthSourceOfOwner.winnings.nature || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.windfall.amount"],
                        value: institution.owner.wealthSourceOfOwner.winnings.amount || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.name.of.entity.issuing.payment"],
                        value:
                          institution.owner.wealthSourceOfOwner.winnings
                            .nameOfEntityIssuingPayment || "-",
                      },
                    ]}
                  />
                </div>
              </>
            ) : null}

            {/* 知识产权 */}
            {institution.owner.wealthSourceOfOwner.intellectualProperty ? (
              <>
                <div className="text-[24px] font-bold mb-6">
                  {crossIntl["kyc.v2.display.section.source.of.wealth.intellectual.property"]}
                </div>
                <div style={{ whiteSpace: "pre-line", marginBottom: 32 }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.type.of.intellectual.property"],
                        value:
                          institution.owner.wealthSourceOfOwner.intellectualProperty.type || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.fund.received"],
                        value:
                          institution.owner.wealthSourceOfOwner.intellectualProperty.fundReceived ||
                          "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.date.of.receipt.of.funds"],
                        value:
                          institution.owner.wealthSourceOfOwner.intellectualProperty.dateReceived ||
                          "-",
                      },
                    ]}
                  />
                </div>
              </>
            ) : null}

            {/* 租金收入 */}
            {institution.owner.wealthSourceOfOwner.rentalIncome ? (
              <>
                <div className="text-[24px] font-bold mb-6">
                  {crossIntl["kyc.v2.display.section.source.of.wealth.rental.income"]}
                </div>
                <div style={{ whiteSpace: "pre-line", marginBottom: 32 }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.rental.property.address"],
                        value:
                          institution.owner.wealthSourceOfOwner.rentalIncome.propertyAddress || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.property.owned.and.rented.period"],
                        value:
                          institution.owner.wealthSourceOfOwner.rentalIncome.periodRented || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.rental.amount"],
                        value:
                          institution.owner.wealthSourceOfOwner.rentalIncome.rentalAmount || "-",
                      },
                    ]}
                  />
                </div>
              </>
            ) : null}

            {/* 其他 */}
            {institution.owner.wealthSourceOfOwner.other ? (
              <>
                <div className="text-[24px] font-bold mb-6">
                  {crossIntl["kyc.v2.display.section.source.of.wealth.other"].replace(
                    "{title}",
                    institution.owner.wealthSourceOfOwner.other.title ||
                      crossIntl["kyc.v2.option.label.other"],
                  )}
                </div>
                <div style={{ whiteSpace: "pre-line", marginBottom: 32 }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.nature.of.source.of.wealth"],
                        value:
                          institution.owner.wealthSourceOfOwner.other.wealthSourceNature || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.date.of.receipt"],
                        value: institution.owner.wealthSourceOfOwner.other.dateReceived || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.display.label.total.amount.received"],
                        value: institution.owner.wealthSourceOfOwner.other.amountReceived || "-",
                      },
                    ]}
                  />
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {/* Director */}
        {institution?.directors && institution.directors.length > 0 ? (
          <div className="mb-8">
            <div className="text-[24px] font-bold mb-6">
              {crossIntl["kyc.v2.display.section.director"]}
            </div>
            <KycInformationTable
              columns={[
                { label: crossIntl["kyc.v2.display.label.director.name"], name: "name" },
                { label: crossIntl["kyc.v2.display.label.nationality"], name: "nationalities" },
                {
                  label: crossIntl["kyc.v2.display.label.residential.address"],
                  name: "residentialAddress",
                },
              ]}
              data={institution.directors.map(item => ({
                name: item.name || "-",
                nationalities: item.nationalities, // 保留原始值，让表格组件处理
                residentialAddress: item.residentialAddress || "-",
              }))}
              crossIntl={crossIntl}
            />
          </div>
        ) : null}

        {/* Authorised signatory */}
        {institution?.authorisedSignatories && institution.authorisedSignatories.length > 0 ? (
          <div className="mb-8">
            <div className="text-[24px] font-bold mb-6">
              {crossIntl["kyc.v2.display.section.authorised.signatory"]}
            </div>
            <KycInformationTable
              columns={[
                {
                  label: crossIntl["kyc.v2.display.label.authorised.signatory.name"],
                  name: "name",
                },
                { label: crossIntl["kyc.v2.display.label.nationality"], name: "nationalities" },
                {
                  label: crossIntl["kyc.v2.display.label.residential.address"],
                  name: "residentialAddress",
                },
              ]}
              data={institution.authorisedSignatories.map(item => ({
                name: item.name || "-",
                nationalities: item.nationalities, // 保留原始值，让表格组件处理
                residentialAddress: item.residentialAddress || "-",
              }))}
              crossIntl={crossIntl}
            />
          </div>
        ) : null}

        {/* Most senior authorised signatory */}
        {institution?.mostSeniorAuthorisedSignatory ? (
          <div className="mb-8">
            <div className="text-[24px] font-bold mb-6">
              {crossIntl["kyc.v2.display.section.most.senior.signatory"]}
            </div>
            <div style={{ whiteSpace: "pre-line" }}>
              <KycKeyValueTable
                data={[
                  {
                    label: crossIntl["kyc.v2.display.label.name"],
                    value: institution.mostSeniorAuthorisedSignatory.name || "-",
                  },
                  {
                    label: crossIntl["kyc.v2.display.label.knowledge.level"],
                    value: formatArrayValue(
                      institution.mostSeniorAuthorisedSignatory.knowledgeLevelOfDerivativesAndFunds,
                    ),
                  },
                ]}
              />
            </div>

            {/* Years of investment / trading experience - Record 类型，表格展示 */}
            {institution.mostSeniorAuthorisedSignatory.yearsOfInvestmentExperience ? (
              <div className="mt-6">
                <div className="text-lg font-semibold mb-4">
                  {crossIntl["kyc.v2.display.label.years.of.investment.experience"]}
                </div>
                <KycInformationTable
                  columns={[
                    { label: crossIntl["kyc.v2.display.table.column.product"], name: "product" },
                    {
                      label: crossIntl["kyc.v2.display.label.years.of.investment.experience"],
                      name: "value",
                    },
                  ]}
                  data={formatRecordToTableData(
                    institution.mostSeniorAuthorisedSignatory.yearsOfInvestmentExperience,
                  )}
                  crossIntl={crossIntl}
                />
              </div>
            ) : null}

            {/* Over the last 5 year, how many investments... - Record 类型，表格展示 */}
            {institution.mostSeniorAuthorisedSignatory.numberOfInvestmentsLastFiveYear ? (
              <div className="mt-6">
                <div className="text-lg font-semibold mb-4">
                  {crossIntl["kyc.v2.display.label.investments.last.five.years"]}
                </div>
                <KycInformationTable
                  columns={[
                    { label: crossIntl["kyc.v2.display.table.column.product"], name: "product" },
                    {
                      label: crossIntl["kyc.v2.display.table.column.number.of.investments"],
                      name: "value",
                    },
                  ]}
                  data={formatRecordToTableData(
                    institution.mostSeniorAuthorisedSignatory.numberOfInvestmentsLastFiveYear,
                  )}
                  crossIntl={crossIntl}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Source of Fund - 根据不同的资金来源类型展示 */}
        {institution?.fundSource ? (
          <div className="mb-8">
            {/* Income from normal operations of the company */}
            {institution.fundSource.normalOperationsOfCompany ? (
              <div className="mb-6">
                <div className="text-[20px] font-medium mb-4">
                  {crossIntl["kyc.v2.display.section.source.of.fund.income"]}
                </div>
              </div>
            ) : null}

            {/* Funding from Shareholder */}
            {institution.fundSource.fundingFromShareholder ? (
              <div className="mb-6">
                <div className="text-[20px] font-medium mb-4">
                  {crossIntl["kyc.v2.display.section.source.of.fund.shareholder"]}
                </div>
                <div style={{ whiteSpace: "pre-line" }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.name.of.shareholder"],
                        value:
                          institution.fundSource.fundingFromShareholder.nameOfShareholder || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.country.of.originating.funds"],
                        value:
                          institution.fundSource.fundingFromShareholder.originatingFundsCountry ||
                          "-",
                      },
                    ]}
                  />
                </div>
              </div>
            ) : null}

            {/* Dividends and returns from managing an investment portfolio */}
            {institution.fundSource.returnsFromInvestment ? (
              <div className="mb-6">
                <div className="text-[20px] font-medium mb-4">
                  {crossIntl["kyc.v2.display.section.source.of.fund.dividends"]}
                </div>
                <div style={{ whiteSpace: "pre-line" }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.description.of.investment"],
                        value: institution.fundSource.returnsFromInvestment.description || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.name.of.investments"],
                        value: institution.fundSource.returnsFromInvestment.name || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.purchased.date"],
                        value: institution.fundSource.returnsFromInvestment.purchasedDate || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.amount.received.per.annum"],
                        value:
                          institution.fundSource.returnsFromInvestment.amountReceivedPerAnnum ||
                          "-",
                      },
                    ]}
                  />
                </div>
              </div>
            ) : null}

            {/* Sale of Assets */}
            {institution.fundSource.saleOfAssets ? (
              <div className="mb-6">
                <div className="text-[20px] font-medium mb-4">
                  {crossIntl["kyc.v2.display.section.source.of.fund.sale.of.assets"]}
                </div>
                <div style={{ whiteSpace: "pre-line" }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.description.of.asset"],
                        value: institution.fundSource.saleOfAssets.description || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.date.of.sale"],
                        value: institution.fundSource.saleOfAssets.saleDate || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.total.sale.amount"],
                        value: institution.fundSource.saleOfAssets.saleAmount || "-",
                      },
                    ]}
                  />
                </div>
              </div>
            ) : null}

            {/* Debt raising */}
            {institution.fundSource.debtRaising ? (
              <div className="mb-6">
                <div className="text-[20px] font-medium mb-4">
                  {crossIntl["kyc.v2.display.section.source.of.fund.debt.raising"]}
                </div>
                <div style={{ whiteSpace: "pre-line" }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.funding.entities"],
                        value: institution.fundSource.debtRaising.fundingEntities || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.country.of.funding.entities"],
                        value: institution.fundSource.debtRaising.countryOfFundingEntities || "-",
                      },
                    ]}
                  />
                </div>
              </div>
            ) : null}

            {/* Other Fund Source */}
            {institution.fundSource.otherFundSource ? (
              <div className="mb-6">
                <div className="text-[20px] font-medium mb-4">
                  {crossIntl["kyc.v2.display.section.source.of.fund.other"].replace(
                    "{title}",
                    institution.fundSource.otherFundSource.title ||
                      crossIntl["kyc.v2.option.label.other"],
                  )}
                </div>
                <div style={{ whiteSpace: "pre-line" }}>
                  <KycKeyValueTable
                    data={[
                      {
                        label: crossIntl["kyc.v2.form.label.nature.of.source.of.wealth"],
                        value: institution.fundSource.otherFundSource.description || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.amount.per.annum"],
                        value: institution.fundSource.otherFundSource.amountPerAnnum || "-",
                      },
                      {
                        label: crossIntl["kyc.v2.form.label.originating.country"],
                        value: institution.fundSource.otherFundSource.originatingCountry || "-",
                      },
                    ]}
                  />
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  // 个人类型渲染
  const questionnaire = kycInfo.individualSurveyQuestionnaire;

  return (
    <div className="">
      {/* Personal Information */}
      {questionnaire?.personalInformation ? (
        <div className="mb-8">
          <div className="text-[24px] font-bold mb-6">
            {crossIntl["kyc.v2.display.section.personal.information"]}
          </div>
          <div style={{ whiteSpace: "pre-line" }}>
            <KycKeyValueTable
              data={[
                {
                  label: crossIntl["kyc.v2.display.label.nationality"],
                  value: getKycInformationNationalityValue(kycInfo.nationality, "nationality"),
                },
                {
                  label: crossIntl["kyc.v2.display.label.multiple.nationalities"],
                  value:
                    getKycInformationNationalityValue(
                      questionnaire.personalInformation.multipleNationalities,
                      "multipleNationalities",
                    ) || questionnaire.personalInformation.multipleNationalities,
                },
                {
                  label: crossIntl["kyc.v2.display.label.politically.exposed.person"],
                  value: questionnaire.personalInformation.politicallyExposedPerson || "-",
                },
                {
                  label: crossIntl["kyc.v2.display.label.investment.annual.income"],
                  value: questionnaire.personalInformation.annualIncome || "-",
                },
                {
                  label: crossIntl["kyc.v2.display.label.liquid.net.worth"],
                  value: questionnaire.personalInformation.liquidNetWorth || "-",
                },
              ]}
            />
          </div>
        </div>
      ) : null}

      {/* Investment */}
      {questionnaire?.investment ? (
        <div className="mb-8">
          <div className="text-[24px] font-bold mb-6">
            {crossIntl["kyc.v2.display.section.investment"]}
          </div>
          <div style={{ whiteSpace: "pre-line" }}>
            <KycKeyValueTable
              data={[
                {
                  label: crossIntl["kyc.v2.display.label.knowledge.level"],
                  value: formatArrayValue(questionnaire.investment.productKnowledgeLevels),
                },
                {
                  label: crossIntl["kyc.v2.display.label.expected.investment.holding.period"],
                  value: questionnaire.investment.expectedInvestmentHoldingPeriod || "-",
                },
                {
                  label: crossIntl["kyc.v2.display.label.expected.account.utilization"],
                  value: questionnaire.investment.expectedAccountUtilization || "-",
                },
              ]}
            />
          </div>

          {/* Years of investment / trading experience - Record 类型，表格展示 */}
          {questionnaire.investment.yearsOfInvestmentExperience ? (
            <div className="mt-6">
              <div className="text-lg font-semibold mb-4">
                {crossIntl["kyc.v2.display.label.years.of.investment.experience"]}
              </div>
              <KycInformationTable
                columns={[
                  { label: crossIntl["kyc.v2.display.table.column.product"], name: "product" },
                  {
                    label: crossIntl["kyc.v2.display.label.years.of.investment.experience"],
                    name: "value",
                  },
                ]}
                data={formatRecordToTableData(questionnaire.investment.yearsOfInvestmentExperience)}
                crossIntl={crossIntl}
              />
            </div>
          ) : null}

          {/* Over the last 5 years, how many investments... - Record 类型，表格展示 */}
          {questionnaire.investment.numberOfInvestmentsLastFiveYear ? (
            <div className="mt-6">
              <div className="text-lg font-semibold mb-4">
                {crossIntl["kyc.v2.display.label.investments.last.five.years"]}
              </div>
              <KycInformationTable
                columns={[
                  { label: crossIntl["kyc.v2.display.table.column.product"], name: "product" },
                  {
                    label: crossIntl["kyc.v2.display.table.column.number.of.investments"],
                    name: "value",
                  },
                ]}
                data={formatRecordToTableData(
                  questionnaire.investment.numberOfInvestmentsLastFiveYear,
                )}
                crossIntl={crossIntl}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Supplement Information */}
      {questionnaire?.supplement ? (
        <div className="mb-8">
          <div className="text-[24px] font-bold mb-6">
            {crossIntl["kyc.v2.display.section.supplement"]}
          </div>

          {/* ========== Source of Fund 部分 ========== */}
          {/* Source of Fund - Employment Income */}
          {questionnaire.supplement.sourceOfFund?.employmentIncome ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.fund.employment.income"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.name.of.employer"],
                      value:
                        questionnaire.supplement.sourceOfFund.employmentIncome.employerName || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.business.sector"],
                      value:
                        questionnaire.supplement.sourceOfFund.employmentIncome.businessSector ||
                        "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.current.annual.salary.and.bonus"],
                      value:
                        questionnaire.supplement.sourceOfFund.employmentIncome
                          .annualSalaryAndBonus || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.country.of.employer"],
                      value: getKycInformationNationalityValue(
                        questionnaire.supplement.sourceOfFund.employmentIncome.employerCountry,
                        "employerCountry",
                      ),
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of Fund - Inheritance */}
          {questionnaire.supplement.sourceOfFund?.inheritance ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.fund.inheritance"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.deceased.name"],
                      value: questionnaire.supplement.sourceOfFund.inheritance.deceasedName || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.relationship.to.customer"],
                      value:
                        questionnaire.supplement.sourceOfFund.inheritance.relationToDeceased || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.date.of.receipt"],
                      value: questionnaire.supplement.sourceOfFund.inheritance.receiptDate || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.inheritance.received"],
                      value:
                        questionnaire.supplement.sourceOfFund.inheritance.amountReceived || "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of Fund - Family Support & Gifts */}
          {questionnaire.supplement.sourceOfFund?.familySupportGift ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.fund.family.support"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.name.of.donor"],
                      value:
                        (questionnaire.supplement.sourceOfFund.familySupportGift as any)
                          ?.donorName || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.relationship"],
                      value:
                        (questionnaire.supplement.sourceOfFund.familySupportGift as any)
                          ?.relationToDonor || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.amount.received"],
                      value:
                        questionnaire.supplement.sourceOfFund.familySupportGift.amountReceived ||
                        "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of Fund - Retirement Income */}
          {questionnaire.supplement.sourceOfFund?.retirementIncome ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.fund.retirement.income"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.name.of.last.employer"],
                      value:
                        (questionnaire.supplement.sourceOfFund.retirementIncome as any)
                          ?.lastEmployerName || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.annual.amount"],
                      value:
                        (questionnaire.supplement.sourceOfFund.retirementIncome as any)
                          ?.annualAmount || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.name.of.entity.issuing.payment"],
                      value:
                        (questionnaire.supplement.sourceOfFund.retirementIncome as any)
                          ?.issuingEntityName || "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of Fund - Sale of Assets */}
          {questionnaire.supplement.sourceOfFund?.assetSale ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.fund.sale.of.assets"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.type.of.asset"],
                      value:
                        questionnaire.supplement.sourceOfFund.assetSale.assetDescription || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.date.of.sale"],
                      value: questionnaire.supplement.sourceOfFund.assetSale.saleDate || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.total.sale.amount"],
                      value: questionnaire.supplement.sourceOfFund.assetSale.saleAmount || "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of Fund - Investment Income */}
          {questionnaire.supplement.sourceOfFund?.investmentIncome ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.fund.investment.income"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.asset.description"],
                      value:
                        (questionnaire.supplement.sourceOfFund.investmentIncome as any)
                          ?.assetDescription || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.investment.name"],
                      value:
                        (questionnaire.supplement.sourceOfFund.investmentIncome as any)
                          ?.investmentName || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.approximate.date.purchased"],
                      value:
                        (questionnaire.supplement.sourceOfFund.investmentIncome as any)
                          ?.purchaseDate || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.annual.profit.and.dividends"],
                      value:
                        (questionnaire.supplement.sourceOfFund.investmentIncome as any)
                          ?.annualProfitAndDividends || "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of Fund - Other */}
          {questionnaire.supplement.sourceOfFund?.other ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.fund.other"]?.replace(
                  "{title}",
                  questionnaire.supplement.sourceOfFund.other.title ||
                    crossIntl["kyc.v2.option.label.other"],
                ) ||
                  `${crossIntl["kyc.v2.display.section.source.of.fund.income"]} - ${
                    questionnaire.supplement.sourceOfFund.other.title ||
                    crossIntl["kyc.v2.option.label.other"]
                  }`}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.description.of.source.of.funds"],
                      value: questionnaire.supplement.sourceOfFund.other.description || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.annual.amount"],
                      value: questionnaire.supplement.sourceOfFund.other.annualAmount || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.country.of.origin"],
                      value: getKycInformationNationalityValue(
                        questionnaire.supplement.sourceOfFund.other.countryOfOrigin,
                        "countryOfOrigin",
                      ),
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* ========== Source of Wealth 部分 ========== */}
          {/* Source of wealth - Employment Income */}
          {questionnaire.supplement.sourceOfWealth?.employmentIncome ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.wealth.salary"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.display.label.employer.name"],
                      value:
                        questionnaire.supplement.sourceOfWealth.employmentIncome.employerName ||
                        "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.employer.address"],
                      value:
                        questionnaire.supplement.sourceOfWealth.employmentIncome.employerAddress ||
                        "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.business.sector"],
                      value:
                        questionnaire.supplement.sourceOfWealth.employmentIncome.businessSector ||
                        "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.salary.per.annum"],
                      value:
                        questionnaire.supplement.sourceOfWealth.employmentIncome.salaryPerAnnum ||
                        "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.years.working.at.employer"],
                      value:
                        questionnaire.supplement.sourceOfWealth.employmentIncome.serviceYears ||
                        "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of wealth - Inheritance */}
          {questionnaire.supplement.sourceOfWealth?.inheritance ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.wealth.inheritance"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.deceased.name"],
                      value:
                        questionnaire.supplement.sourceOfWealth.inheritance.deceasedName || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.date.of.death"],
                      value: questionnaire.supplement.sourceOfWealth.inheritance.deathDate || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.relationship.to.customer"],
                      value:
                        questionnaire.supplement.sourceOfWealth.inheritance.relationship || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.inheritance.received"],
                      value:
                        questionnaire.supplement.sourceOfWealth.inheritance.amountReceived || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.date.of.receipt"],
                      value:
                        questionnaire.supplement.sourceOfWealth.inheritance.receivedDate || "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of wealth - Family Support & Gifts */}
          {questionnaire.supplement.sourceOfWealth?.familySupportGift ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.wealth.gifts"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.date.of.receipt"],
                      value:
                        questionnaire.supplement.sourceOfWealth.familySupportGift.receivedDate ||
                        "-",
                    },
                    {
                      label: crossIntl["kyc.v2.display.label.total.amount.received"],
                      value:
                        questionnaire.supplement.sourceOfWealth.familySupportGift.receivedAmount ||
                        "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.relationship.to.ubo"],
                      value:
                        questionnaire.supplement.sourceOfWealth.familySupportGift
                          .relationshipToUBO || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.reason.for.gift"],
                      value:
                        questionnaire.supplement.sourceOfWealth.familySupportGift.giftReason || "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of wealth - Retirement Income */}
          {questionnaire.supplement.sourceOfWealth?.retirementIncome ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.wealth.other"].replace(
                  "{title}",
                  crossIntl["kyc.v2.option.label.retirement.income"],
                )}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.name.of.last.employer"],
                      value:
                        (questionnaire.supplement.sourceOfWealth.retirementIncome as any)
                          ?.lastEmployerName || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.annual.amount"],
                      value:
                        (questionnaire.supplement.sourceOfWealth.retirementIncome as any)
                          ?.annualAmount || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.name.of.entity.issuing.payment"],
                      value:
                        (questionnaire.supplement.sourceOfWealth.retirementIncome as any)
                          ?.issuingEntityName || "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of wealth - Sale of Assets */}
          {questionnaire.supplement.sourceOfWealth?.assetSale ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.wealth.asset.sale"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.type.of.asset"],
                      value: questionnaire.supplement.sourceOfWealth.assetSale.assetType || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.date.of.sale"],
                      value: questionnaire.supplement.sourceOfWealth.assetSale.saleDate || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.total.sale.amount"],
                      value: questionnaire.supplement.sourceOfWealth.assetSale.saleAmount || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.how.long.asset.owned"],
                      value: questionnaire.supplement.sourceOfWealth.assetSale.holdPeriod || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.originating.source.of.asset"],
                      value:
                        questionnaire.supplement.sourceOfWealth.assetSale.originatingSource || "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of wealth - Investment Income */}
          {questionnaire.supplement.sourceOfWealth?.investmentIncome ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.wealth.other"].replace(
                  "{title}",
                  crossIntl["kyc.v2.option.label.investment.income"],
                )}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.display.label.date.of.receipt.of.dividend"],
                      value:
                        questionnaire.supplement.sourceOfWealth.investmentIncome.receiptDate || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.display.label.total.amount.received"],
                      value:
                        questionnaire.supplement.sourceOfWealth.investmentIncome.receivedAmount ||
                        "-",
                    },
                    {
                      label: crossIntl["kyc.v2.display.label.company.paying.dividend"],
                      value:
                        questionnaire.supplement.sourceOfWealth.investmentIncome
                          .payingCompanyNameAndTradedAsset || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.display.label.length.of.time.shares.held"],
                      value:
                        questionnaire.supplement.sourceOfWealth.investmentIncome.holdPeriod || "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of wealth - Self Employment and Business Ownership */}
          {questionnaire.supplement.sourceOfWealth?.selfEmployment ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.wealth.self.employment"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.name.and.address.of.business"],
                      value:
                        questionnaire.supplement.sourceOfWealth.selfEmployment.nameAndAddress ||
                        "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.annual.profit"],
                      value:
                        questionnaire.supplement.sourceOfWealth.selfEmployment.annualProfit || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.nature.of.business"],
                      value:
                        questionnaire.supplement.sourceOfWealth.selfEmployment.natureOfBusiness ||
                        "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.ownership.percentage.in.business"],
                      value:
                        questionnaire.supplement.sourceOfWealth.selfEmployment
                          .ownershipPercentage || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.business.years.in.operation"],
                      value:
                        questionnaire.supplement.sourceOfWealth.selfEmployment.businessYears || "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of wealth - Rental Income */}
          {questionnaire.supplement.sourceOfWealth?.rentalIncome ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.wealth.rental.income"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.rental.property.address"],
                      value:
                        questionnaire.supplement.sourceOfWealth.rentalIncome.propertyAddress || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.property.owned.and.rented.period"],
                      value:
                        questionnaire.supplement.sourceOfWealth.rentalIncome.periodRented || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.rental.amount"],
                      value:
                        questionnaire.supplement.sourceOfWealth.rentalIncome.annualRentalIncome ||
                        "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of wealth - Legal settlement */}
          {questionnaire.supplement.sourceOfWealth?.legalSettlement ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.wealth.legal.settlements"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.details.of.legal.settlement"],
                      value: questionnaire.supplement.sourceOfWealth.legalSettlement.details || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.payment.issuing.party"],
                      value:
                        questionnaire.supplement.sourceOfWealth.legalSettlement
                          .paymentIssuingParty || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.settlement.amount"],
                      value:
                        questionnaire.supplement.sourceOfWealth.legalSettlement.settlementAmount ||
                        "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.settlement.date"],
                      value:
                        questionnaire.supplement.sourceOfWealth.legalSettlement.settlementDate ||
                        "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of wealth - Winnings / Windfall */}
          {questionnaire.supplement.sourceOfWealth?.winnings ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.wealth.winnings"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.date.of.winning.windfall"],
                      value: questionnaire.supplement.sourceOfWealth.winnings.winningsDate || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.nature.of.windfall"],
                      value: questionnaire.supplement.sourceOfWealth.winnings.windfallNature || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.windfall.amount"],
                      value: questionnaire.supplement.sourceOfWealth.winnings.windfallAmount || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.name.of.entity.issuing.payment"],
                      value:
                        questionnaire.supplement.sourceOfWealth.winnings.nameOfIssuingPayment ||
                        "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of wealth - Intellectual Property */}
          {questionnaire.supplement.sourceOfWealth?.intellectualProperty ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.wealth.intellectual.property"]}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.type.of.intellectual.property"],
                      value:
                        questionnaire.supplement.sourceOfWealth.intellectualProperty.type || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.fund.received"],
                      value:
                        questionnaire.supplement.sourceOfWealth.intellectualProperty
                          .amountReceived || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.date.of.receipt.of.funds"],
                      value:
                        questionnaire.supplement.sourceOfWealth.intellectualProperty.dateReceived ||
                        "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* Source of wealth - Other */}
          {questionnaire.supplement.sourceOfWealth?.other ? (
            <div className="mb-6">
              <div className="text-[20px] font-medium mb-4">
                {crossIntl["kyc.v2.display.section.source.of.wealth.other"]?.replace(
                  "{title}",
                  questionnaire.supplement.sourceOfWealth.other.title ||
                    crossIntl["kyc.v2.option.label.other"],
                ) ||
                  `${crossIntl["kyc.v2.display.section.source.of.wealth.salary"]} - ${
                    questionnaire.supplement.sourceOfWealth.other.title ||
                    crossIntl["kyc.v2.option.label.other"]
                  }`}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                <KycKeyValueTable
                  data={[
                    {
                      label: crossIntl["kyc.v2.form.label.nature.of.source.of.wealth"],
                      value: questionnaire.supplement.sourceOfWealth.other.nature || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.form.label.date.of.receipt"],
                      value: questionnaire.supplement.sourceOfWealth.other.receivedDate || "-",
                    },
                    {
                      label: crossIntl["kyc.v2.display.label.total.amount.received"],
                      value: questionnaire.supplement.sourceOfWealth.other.receivedAmount || "-",
                    },
                  ]}
                />
              </div>
            </div>
          ) : null}

          {/* ========== 其他补充字段 ========== */}
          <div style={{ whiteSpace: "pre-line" }}>
            <KycKeyValueTable
              data={[
                {
                  label: crossIntl["kyc.v2.display.label.employment.status"],
                  value: questionnaire.supplement.employmentStatus || "-",
                },
                {
                  label: crossIntl["kyc.v2.display.label.employer.name"],
                  value: questionnaire.supplement.employerName || "-",
                },
                {
                  label: crossIntl["kyc.v2.display.label.employer.country"],
                  value: getKycInformationNationalityValue(
                    questionnaire.supplement.employerCountry,
                    "employerCountry",
                  ),
                },
                {
                  label: crossIntl["kyc.v2.display.label.employer.industry"],
                  value: questionnaire.supplement.employerIndustry || "-",
                },
                {
                  label: crossIntl["kyc.v2.display.label.intend.to.fund.account"],
                  value: formatArrayValue(questionnaire.supplement.accountFundingMethods),
                },
                {
                  label: crossIntl["kyc.v2.display.label.funding.account.owner.name"],
                  value: questionnaire.supplement.fundingAccountOwnerName || "-",
                },
                {
                  label: crossIntl["kyc.v2.display.label.intend.to.deposit.amount"],
                  value: questionnaire.supplement.intendedDepositAmount || "-",
                },
                {
                  label: crossIntl["kyc.v2.display.label.expected.trading.volume"],
                  value: questionnaire.supplement.expectedTradingVolume || "-",
                },
                {
                  label: crossIntl["kyc.v2.display.label.funding.source.country"],
                  value: getKycInformationNationalityValue(
                    questionnaire.supplement.fundingSourceCountry,
                    "fundingSourceCountry",
                  ),
                },
                {
                  label: crossIntl["kyc.v2.display.label.funding.source.institution"],
                  value: questionnaire.supplement.fundingSourceInstitution || "-",
                },
                {
                  label: crossIntl["kyc.v2.display.label.desired.product.types"],
                  value: formatArrayValue(questionnaire.supplement.desiredProductTypes),
                },
              ]}
            />
          </div>
        </div>
      ) : null}

      {/* Tax Residence */}
      {questionnaire?.taxResidences ? (
        <div className="mb-8">
          <div className="text-[24px] font-bold mb-6">
            {crossIntl["kyc.v2.display.section.tax.residence"]}
          </div>
          <div style={{ whiteSpace: "pre-line" }}>
            <KycKeyValueTable
              data={[
                {
                  label: crossIntl["kyc.v2.display.label.us.tax.resident"],
                  value: questionnaire.taxResidences.taxCitizenFromUS || "-",
                },
              ]}
            />
          </div>
          {questionnaire.taxResidences.taxResidenceList &&
          questionnaire.taxResidences.taxResidenceList.length > 0 ? (
            <div className="mt-6">
              <KycInformationTable
                columns={[
                  {
                    label: crossIntl["kyc.v2.display.label.tax.residence.country"],
                    name: "country",
                  },
                  { label: crossIntl["kyc.v2.display.label.tin"], name: "tin" },
                  {
                    label: crossIntl["kyc.v2.display.label.reason.of.unavailability"],
                    name: "unavailableReason",
                  },
                ]}
                data={questionnaire.taxResidences.taxResidenceList.map(item => ({
                  country: item.country, // 保留原始值，让表格组件处理
                  tin: item.tin || "-",
                  unavailableReason: item.unavailableReason || "-",
                }))}
                crossIntl={crossIntl}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Professional Experience */}
      {questionnaire?.professionalExperience ? (
        <div className="mb-8">
          <div className="text-[24px] font-bold mb-6">
            {crossIntl["kyc.v2.display.section.professional.experience"]}
          </div>
          <div style={{ whiteSpace: "pre-line" }}>
            <KycKeyValueTable
              data={[
                {
                  label: crossIntl["kyc.v2.display.label.linkedin.profile"],
                  value: questionnaire.professionalExperience.linkedinProfile || "-",
                },
              ]}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};
